"use client";

// Сегментированный переключатель портала: ряд вариантов, из которых выбран один.
//
// ЗАЧЕМ ОТДЕЛЬНЫМ КОМПОНЕНТОМ. Такие ряды («Вакансии / Кандидаты / Воронка /
// Аналитика», периоды в отчётах) собирались руками в пяти местах: своя обёртка,
// свои кнопки, своя заливка активного. Отсюда и разнобой — где-то обёртка
// осталась капсулой при прямоугольных кнопках внутри (Максим, 26.08.2026).
//
// Здесь же живёт и движение: заливка не перекрашивается скачком, а ПЕРЕЕЗЖАЕТ
// к нажатому варианту (№9 каталога движения). Взгляд следует за ней и не теряет
// место в ряду.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export type SegmentedItem<T extends string> = { value: T; label: string };

export function Segmented<T extends string>({
  items,
  value,
  onChange,
  accent,
  className,
}: {
  items: readonly SegmentedItem<T>[];
  value: T;
  onChange: (next: T) => void;
  /** Цвет активного указателя. По умолчанию фирменный. */
  accent?: string;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const lineRef = useRef<HTMLSpanElement | null>(null);
  const [animated, setAnimated] = useState(false);

  // ⚠️ УКАЗАТЕЛЬ ОДИН — ПОЛОСА (Максим, 26.08.2026). Обводка под курсором тут
  // была и уехала: в ряду из четырёх коротких вариантов две движущиеся вещи
  // спорили друг с другом. Отклик на курсор остался — подпись светлеет.
  //
  // Позиции пишем прямо в DOM: эффект работает на каждом рендере, и setState
  // внутри него зацикливает обновления (тот же приём, что у подсветки меню).
  const place = useCallback(() => {
    const host = hostRef.current;
    if (!host) return;
    const h = host.getBoundingClientRect();
    const box = (key: string | null) => {
      const b = key ? btnRefs.current[key] : null;
      if (!b) return null;
      const r = b.getBoundingClientRect();
      return { left: r.left - h.left + host.scrollLeft, width: r.width };
    };

    const active = box(value);
    if (lineRef.current && active) {
      lineRef.current.style.transform = `translateX(${active.left}px)`;
      lineRef.current.style.width = `${active.width}px`;
      lineRef.current.style.opacity = "1";
    }

  }, [value]);

  useLayoutEffect(() => { place(); });

  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => place());
    ro.observe(host);
    return () => ro.disconnect();
  }, [place]);

  return (
    <div
      ref={hostRef}
      className={`segmented ${className ?? ""}`}
    >
      <span
        ref={lineRef}
        aria-hidden
        className={`segmented-line${animated ? " is-animated" : ""}`}
        style={{ position: "absolute", opacity: 0, ...(accent ? { background: accent } : null) }}
      />
      {items.map((it) => (
        <button
          key={it.value}
          ref={(el) => { btnRefs.current[it.value] = el; }}
          type="button"
          onClick={() => onChange(it.value)}
          aria-pressed={value === it.value}
          className="segmented-btn"
          style={{ boxShadow: "none" }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}
